import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { EntryPoint, EntryPointService, GroupService, NewGroup } from '@mirage-projects/cw-commons';
import { CatalogObjectTypes, Group, PermAccessLevelString } from '@mirage-projects/cw-models';

@Injectable({
    providedIn: 'root',
})
export class AppGroupService implements GroupService {
    constructor(
        private http: HttpClient,
        private entryPointService: EntryPointService
    ) {}

    get(id: string): Observable<Group> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.GROUPS, id);
        return this.http.get<Group>(`${entryPoint}`, {}).pipe(catchError((err) => throwError(err)));
    }

    getAll(): Observable<Group[]> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.GROUPS);
        return this.http.get<Group[]>(`${entryPoint}`, {}).pipe(catchError((err) => throwError(err)));
    }

    new(group: NewGroup): Observable<Group> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.GROUPS);
        return this.http.post<Group>(`${entryPoint}`, group).pipe(catchError((err) => throwError(err)));
    }

    update(uid: Group['uid'], group: Partial<Omit<Group, 'Id'>>): Observable<Group> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.GROUPS, uid);
        return this.http.patch<Group>(`${entryPoint}`, group).pipe(catchError((err) => throwError(err)));
    }

    delete(id: string): Observable<any> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.GROUPS, id);
        return this.http.delete(`${entryPoint}`);
    }

    addGroupUsers(id: string, userIds: { uid: string }[]): Observable<unknown> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.GROUP_USERS, id);
        return this.http.post<unknown>(`${entryPoint}`, userIds);
    }

    removeGroupUser(groupId: string, userId: string): Observable<unknown> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.GROUP_USERS, groupId);
        return this.http.delete<unknown>(`${entryPoint}${userId}`);
    }

    addGroupACL(groupId: string, aclId: string): Observable<unknown> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.ACL_GROUPS, groupId);
        return this.http.post<void>(`${entryPoint}`, { uid: aclId });
    }

    deleteGroupACL(groupId: string, aclId: string): Observable<unknown> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.ACL_GROUPS, groupId) + aclId;
        return this.http.delete<unknown>(entryPoint);
    }

    /**
     * Add group permission
     */
    addGroupPermission(
        groupUid: string,
        objectId: number,
        objectType: CatalogObjectTypes,
        objEffect: PermAccessLevelString
    ): Observable<void> {
        const entryPoint = `${this.entryPointService.getResource(EntryPoint.PERMISSIONS)}group/${groupUid}`;
        const perm = {
            id: objectId,
            type: objectType,
            effect: objEffect,
        };
        return this.http.post<void>(`${entryPoint}`, perm);
    }

    /**
     * Delete group permission
     */
    deleteGroupPermission(groupUid: string, objectId: number, objectType: CatalogObjectTypes): Observable<void> {
        const entryPoint = `${this.entryPointService.getResource(EntryPoint.PERMISSIONS)}group/${groupUid}`;
        const perm = {
            id: objectId,
            type: objectType,
        };
        return this.http.delete<void>(`${entryPoint}`, { body: perm });
    }
}
